"use client";

import { useClimateState, useClimateIndex } from "./use-climate-state";
import type { SourceKey } from "@/lib/climate/evidence";
import { SectionTitle, Segments, Sources } from "./ui";
import s from "./climate.module.css";

const STORIES: {
  value: string;
  label: string;
  source: SourceKey;
  loop: boolean;
  steps: { title: string; text: string }[];
}[] = [
  {
    value: "permafrost",
    label: "Permafrost & carbon",
    source: "permafrost",
    loop: true,
    steps: [
      {
        title: "Frozen ground warms",
        text: "Permafrost is ground that stays frozen for at least two consecutive years. In carbon-rich Arctic soils it locks away large stores of organic material.",
      },
      {
        title: "Thaw exposes organic matter",
        text: "As the ground thaws, microbes can break down previously frozen material. The speed depends on temperature, moisture and the type of soil.",
      },
      {
        title: "Carbon returns to the atmosphere",
        text: "Decomposition releases carbon dioxide. In oxygen-poor, waterlogged conditions it can also release methane, a powerful greenhouse gas.",
      },
      {
        title: "Extra warming drives further thaw",
        text: "Those emissions amplify warming, which can cause more thaw. This is a reinforcing feedback with uncertain magnitude and timing, not a claim of an immediate runaway.",
      },
    ],
  },
  {
    value: "albedo",
    label: "Snow & sunlight",
    source: "mountains",
    loop: true,
    steps: [
      {
        title: "Snow cover shrinks",
        text: "Warming reduces the area and duration of snow cover in many mountain regions. Less snow leaves darker ground exposed for longer.",
      },
      {
        title: "Darker ground absorbs more sunlight",
        text: "Bright snow reflects much of the incoming sunlight. Dark rock, soil and vegetation absorb more of that energy.",
      },
      {
        title: "The surface warms further",
        text: "More absorbed energy reinforces local warming and helps melt remaining snow. The strength varies with season, terrain and vegetation.",
      },
      {
        title: "The snow season faces more pressure",
        text: "Earlier melt changes runoff timing, habitat and the weeks available for snow sports. This feedback sits alongside changes in snowfall and air temperature.",
      },
    ],
  },
  {
    value: "ocean",
    label: "Ocean & marine life",
    source: "oceanLife",
    loop: false,
    steps: [
      {
        title: "The ocean accumulates heat",
        text: "A warming ocean exposes marine ecosystems to higher average temperatures and marine heatwaves. This story is a chain of impacts, rather than a closed feedback loop.",
      },
      {
        title: "Habitats become less familiar",
        text: "Thermal stress can bleach corals and damage kelp and seagrass. Warming and oxygen changes alter where different species can thrive.",
      },
      {
        title: "Species and food webs respond",
        text: "Some species shift their ranges; others struggle to move or adapt. Fisheries outcomes also depend on habitat, fishing pressure and management.",
      },
      {
        title: "Coastal livelihoods feel the change",
        text: "Reef condition, biodiversity and fisheries affect diving, tourism and coastal communities. Local impacts require local evidence, not a single global percentage.",
      },
    ],
  },
];

export function FeedbackStories() {
  const [story, setStory] = useClimateState(
    "story",
    "permafrost",
    STORIES.map((item) => item.value),
  );
  const [step, setStep] = useClimateIndex("step", 0, 4);
  const active = STORIES.find((item) => item.value === story)!;
  return (
    <section className={s.dark} id="connections">
      <div className={s.wrap}>
        <SectionTitle
          number="03 / Follow the connections"
          title="One change can set another in motion."
        >
          Explore the processes that connect a warming planet to the snow, seas
          and ecosystems around us.
        </SectionTitle>
        <Segments
          label="Climate process"
          options={STORIES}
          value={story}
          onChange={(value) => {
            setStory(value);
            setStep(0);
          }}
        />
        <div className={s.feedbackGrid}>
          <div
            className={s.feedbackSteps}
            role="group"
            aria-label="Steps in the climate process"
          >
            {active.steps.map((item, index) => (
              <button
                key={`${story}-${index}`}
                type="button"
                aria-pressed={index === step}
                onClick={() => setStep(index)}
              >
                <span className={s.stepNumber}>0{index + 1}</span>
                {item.title}
                <span className="ml-auto" aria-hidden="true">
                  ↗
                </span>
              </button>
            ))}
          </div>
          <div className={s.feedbackVisual} aria-live="polite">
            <svg viewBox="0 0 170 120" className={s.loop} aria-hidden="true">
              <path
                d={
                  active.loop
                    ? "M35 70C5 5 135 -10 145 50M138 39 145 53 153 38M138 62C160 122 35 140 23 76M16 88 23 74 32 87"
                    : "M15 60H155M143 49 155 60 143 71"
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              {[0, 1, 2, 3].map((n) => (
                <circle
                  key={n}
                  cx={active.loop ? [45, 126, 131, 48][n] : 25 + n * 40}
                  cy={active.loop ? [24, 25, 100, 101][n] : 60}
                  r={n === step ? 9 : 5}
                  fill={n === step ? "#F0BE77" : "#5BC0CE"}
                />
              ))}
            </svg>
            <div className={`${s.eyebrow} text-center`}>
              {active.loop ? "Reinforcing feedback" : "Chain of impacts"} · step{" "}
              {step + 1} / 4
            </div>
            <h3>{active.steps[step].title}</h3>
            <p>{active.steps[step].text}</p>
          </div>
        </div>
        <Sources ids={[active.source]} />
        <div className="mt-8 border-t border-white/20 pt-7">
          <h3>And the jet stream or Atlantic circulation?</h3>
          <p className={`${s.small} mt-3`}>
            They matter, but they are different systems. Changes in atmospheric
            circulation can shift regional weather; a “jet stream breakdown” is
            not an established local forecast. Atlantic overturning is expected
            to weaken, while the timing and risk of a collapse remain uncertain.
            Neither supports a guaranteed future ski season for a particular
            resort.
          </p>
          <Sources ids={["circulation"]} />
        </div>
      </div>
    </section>
  );
}
